/**
 * Aufrufe der Wissensdatenbank -- F4-09 bis F4-16.
 *
 * Der Stand wechselt ueber benannte Aktionen und nicht ueber ein Feld: Die
 * Oberflaeche bietet "Zur Pruefung geben" und "Freigeben" an, weil der Server
 * genau das anbietet -- und weil ein Auswahlfeld mit vier Staenden dazu
 * einlaedt, den mittleren zu ueberspringen.
 */

import type { components } from '../../api/schema';
import { download, request, upload } from '../../lib/apiClient';

export type KbArticle = components['schemas']['KbArticleRead'];
export type KbArticleListItem = components['schemas']['KbArticleListItem'];
export type KbArticleList = components['schemas']['KbArticleList'];
export type KbArticleVersion = components['schemas']['KbArticleVersionRead'];
export type KbArticleVersionDetail = components['schemas']['KbArticleVersionDetail'];
export type KbCategory = components['schemas']['KbCategoryRead'];
export type ArticleStatus = components['schemas']['ArticleStatus'];
export type ArticleVisibility = components['schemas']['ArticleVisibilityLevel'];
export type KbAttachment = components['schemas']['AttachmentRead'];

export interface ArticleFilters {
  q?: string;
  category_id?: string;
  status?: ArticleStatus;
  /** Was bei diesem Kunden gilt -- einschliesslich der allgemein gueltigen. */
  customer_id?: string;
  tag_id?: string;
}

/**
 * Eine Seite der Artikelliste -- F4-27.
 *
 * Vorher ein blankes Feld, und damit endete die Wissensdatenbank nach
 * fuenfundzwanzig Eintraegen: Die uebrigen waren ueber die Oberflaeche nicht
 * erreichbar, und nichts sagte, dass es sie gibt.
 */
export function searchArticles(filters: ArticleFilters, cursor?: string): Promise<KbArticleList> {
  return request<KbArticleList>('/kb-articles', {
    query: { ...filters, ...(cursor ? { cursor } : {}) },
  });
}

export function suggestArticles(text: string): Promise<KbArticleListItem[]> {
  return request<KbArticleListItem[]>('/kb-articles/suggestions', { query: { text } });
}

export function getArticle(articleId: string): Promise<KbArticle> {
  return request<KbArticle>(`/kb-articles/${articleId}`);
}

export function createArticle(payload: Record<string, unknown>): Promise<KbArticle> {
  return request<KbArticle>('/kb-articles', { method: 'POST', body: payload });
}

export function updateArticle(
  articleId: string,
  payload: Record<string, unknown>,
): Promise<KbArticle> {
  return request<KbArticle>(`/kb-articles/${articleId}`, { method: 'PATCH', body: payload });
}

export function deleteArticle(articleId: string): Promise<undefined> {
  return request<undefined>(`/kb-articles/${articleId}`, { method: 'DELETE' });
}

/** Die vier Uebergaenge des Freigabelaufs -- F4-11. */
export type ArticleAction = 'submit' | 'publish' | 'reject' | 'archive';

export function runArticleAction(articleId: string, action: ArticleAction): Promise<KbArticle> {
  return request<KbArticle>(`/kb-articles/${articleId}/actions/${action}`, { method: 'POST' });
}

export function createArticleFromTicket(payload: Record<string, unknown>): Promise<KbArticle> {
  return request<KbArticle>('/kb-articles/actions/from-ticket', {
    method: 'POST',
    body: payload,
  });
}

export function listArticleVersions(articleId: string): Promise<KbArticleVersion[]> {
  return request<KbArticleVersion[]>(`/kb-articles/${articleId}/versions`);
}

/**
 * Eine Fassung mit ihrem Inhalt -- F4-34.
 *
 * Die Liste daneben sagt, **dass** etwas geaendert wurde. Erst dieser Aufruf
 * sagt, **was** dort stand -- und genau darum werden Fassungen gefuehrt.
 */
export function getArticleVersion(
  articleId: string,
  versionId: string,
): Promise<KbArticleVersionDetail> {
  return request<KbArticleVersionDetail>(`/kb-articles/${articleId}/versions/${versionId}`);
}

export function rateArticle(articleId: string, rating: number, comment = ''): Promise<KbArticle> {
  return request<KbArticle>(`/kb-articles/${articleId}/feedback`, {
    method: 'POST',
    body: { rating, comment },
  });
}

export function listKbCategories(): Promise<KbCategory[]> {
  return request<KbCategory[]>('/kb-categories');
}

export function createKbCategory(payload: Record<string, unknown>): Promise<KbCategory> {
  return request<KbCategory>('/kb-categories', { method: 'POST', body: payload });
}

export function deleteKbCategory(categoryId: string): Promise<undefined> {
  return request<undefined>(`/kb-categories/${categoryId}`, { method: 'DELETE' });
}

/**
 * Erinnert an faellige Ueberpruefungen -- F4-16.
 *
 * Legt **ein** Ticket fuer alle faelligen Artikel an, nicht eines je
 * Artikel. Denselben Lauf macht der Hintergrunddienst taeglich; hier steht
 * der Ausloeser von Hand, damit sich nachsehen laesst, ob ueberhaupt etwas
 * faellig ist.
 */
export function remindKbReviews(): Promise<{ reminded: number }> {
  return request<{ reminded: number }>('/kb-articles/actions/remind-reviews', { method: 'POST' });
}

// ---------------------------------------------------------------------------
// Anhaenge am Artikel -- F4-09, K-27
// ---------------------------------------------------------------------------
//
// Eigene Pfade und nicht die des Ticketmoduls, obwohl die Dateien in derselben
// Tabelle liegen: Dort haengt das Recht am Ticket, hier am Artikel.

/**
 * Die Dateien eines Artikels.
 *
 * Ohne ``versionId`` die der geltenden Fassung. Mit einer Kennung die einer
 * aelteren -- eine Fassung ist der Nachweis, was zu einem Zeitpunkt dort
 * stand, und die Anlage von damals gehoert dazu (F4-24, F4-34).
 */
export function listArticleAttachments(
  articleId: string,
  versionId?: string,
): Promise<KbAttachment[]> {
  return request<KbAttachment[]>(`/kb-articles/${articleId}/attachments`, {
    query: versionId === undefined ? undefined : { version_id: versionId },
  });
}

export function uploadArticleAttachment(articleId: string, file: File): Promise<KbAttachment> {
  return upload<KbAttachment>(`/kb-articles/${articleId}/attachments`, file);
}

/**
 * Entfernt einen Anhang -- ueber eine neue Fassung, nicht durch Loeschen.
 *
 * F4-24: Fassungen sind unveraenderlich. Die Antwort ist deshalb der Artikel
 * mit seiner neuen Fassung und nicht ein leeres 204.
 */
export function removeArticleAttachment(
  articleId: string,
  attachmentId: string,
): Promise<KbArticle> {
  return request<KbArticle>(`/kb-articles/${articleId}/attachments/${attachmentId}`, {
    method: 'DELETE',
  });
}

export function downloadArticleAttachment(articleId: string, attachmentId: string) {
  return download(`/kb-articles/${articleId}/attachments/${attachmentId}/content`);
}
